import { supabase } from '@/lib/supabase'
import type { OpportunityItem } from '../types'
import { fetchAssetsMeta } from '../utils/assets'
import { detectNearBreak } from '../utils/nearBreak'
import { listConfluences, confluencePercent } from '../utils/confluence'
import { getAllowedSymbolsForCurrentUser } from '@/modules/plans/services/planAssetPolicyService'

const KNOWN_CONFLUENCES = ['rsi', 'ema', 'volume', 'fibo']

// SSOT helpers
function tfShort(tf?: string | null): string | null {
  const t = String(tf || '').toUpperCase()
  if (!t) return null
  if (t === '5') return '5min'
  if (t === '15') return '15min'
  if (t === '30') return '30min'
  if (t === '60') return '1h'
  if (t === '240') return '4h'
  if (t === '720') return '12h'
  if (t === 'D') return '1D'
  if (t === 'W') return '1W'
  if (t === 'M') return '1M'
  return t
}
function minorForMajor(tfRaw?: string | null): string | null {
  const tf = String(tfRaw || '').toUpperCase()
  if (!tf) return null
  if (tf === '60') return '5'
  if (tf === '240') return '15'
  if (tf === '720') return '30'
  if (tf === 'D') return '60'
  if (tf === 'W') return '240'
  if (tf === 'M') return 'D'
  if (tf === '5' || tf === '15' || tf === '30') return tf
  return null
}
function deriveTrendSSOT(row: any): string | null {
  try {
    const cf = row?.confluence_factors || {}
    const t = String(row?.target_trend ?? row?.trend ?? cf.trend_alvo ?? cf.target_trend ?? '').toLowerCase()
    return t || null
  } catch { return null }
}
function toObjective(trend?: string | null): 'Long' | 'Short' | '' {
  const t = String(trend || '').toLowerCase()
  if (t === 'bullish' || t === 'alta' || t === 'long') return 'Long'
  if (t === 'bearish' || t === 'baixa' || t === 'short') return 'Short'
  return ''
}
function toRecommendation(row: any): string | null {
  const tf = String(row?.target_timeframe || '').toUpperCase()
  if (tf === '60') return 'Scalp Trade'
  if (tf === '240') return 'Scalp / Day Trade'
  if (tf === '720') return 'Day Trade'
  if (tf === 'D') return 'Day / Swing Trade'
  if (tf === 'W') return 'Swing Trade'
  if (tf === 'M') return 'Swing / Position Trade'
  const tf2 = String(row?.timeframe || '').toUpperCase()
  if (tf2 === '60') return 'Scalp Trade'
  if (tf2 === '240') return 'Scalp / Day Trade'
  if (tf2 === '720') return 'Day Trade'
  if (tf2 === 'D') return 'Day / Swing Trade'
  if (tf2 === 'W') return 'Swing Trade'
  if (tf2 === 'M') return 'Swing / Position Trade'
  if (tf2 === '5' || tf2 === '15' || tf2 === '30') return 'Scalp Trade'
  return null
}

export async function listOportunidades(): Promise<OpportunityItem[]> {
  // Gate por plano: moedas permitidas (server-side)
  let allowedSymbols: string[] | null = null
  try {
    allowedSymbols = await getAllowedSymbolsForCurrentUser()
  } catch {
    allowedSymbols = null
  }
  const normAllowed = allowedSymbols && allowedSymbols.length
    ? Array.from(new Set(allowedSymbols.map((s) => String(s || '').trim().toUpperCase()).filter(Boolean)))
    : null

  // Conta quantas oportunidades ativas existem para este usuÇ­rio,
  // usando o mesmo filtro de sÇðmbolos permitido do plano.
  let total: number | null = null
  try {
    let countQuery = supabase
      .from('v_trading_opportunities_dedup')
      .select('id', { count: 'exact', head: true })
      .eq('is_published', true)
      .not('published_at', 'is', null)

    if (normAllowed && normAllowed.length > 0) {
      countQuery = countQuery.in('symbol', normAllowed)
    }

    const { count, error: countError } = await countQuery
    if (!countError && typeof count === 'number') {
      total = count
    }
  } catch {
    total = null
  }

  let query = supabase
    .from('v_trading_opportunities_dedup')
    .select('id,symbol,timeframe,target_timeframe,opportunity_type,signal_type,target_trend,confluence_strength,confluence_factors,created_at,open_time,status,closed_at,closed_reason,is_published,published_at')
    .eq('is_published', true)
    .not('published_at', 'is', null)
    .order('published_at', { ascending: false })
    .order('created_at', { ascending: false })

  if (normAllowed && normAllowed.length > 0) {
    query = query.in('symbol', normAllowed)
  }

  if (typeof total === 'number' && total > 0) {
    query = query.range(0, total - 1)
  } else {
    // Fallback defensivo caso o count falhe: lote suficientemente amplo
    // para cenÇ­rios atuais, sem truncar silenciosamente a lista.
    query = query.limit(200)
  }

  const { data: oppsRaw, error } = await query
  if (error) throw error
  const opps = Array.isArray(oppsRaw) ? oppsRaw : []

  const symbols = Array.from(new Set(opps.map((o: any) => String(o.symbol || '').trim()).filter(Boolean)))
  const assets = await fetchAssetsMeta(symbols)
  const bySymbol = new Map(assets.map((a) => [String(a.symbol).toUpperCase(), a]))

  // Filtra quaisquer oportunidades com símbolo sem meta conhecida (evita cards E2E/fakes)
  const filteredOpps = opps.filter((r: any) => {
    const sym = String(r.symbol || '').trim().toUpperCase()
    return bySymbol.has(sym)
  })

  const totalPossible = (row: any): number => {
    try {
      const set = new Set<string>(KNOWN_CONFLUENCES)
      const cf = row?.confluence_factors
      if (cf && typeof cf === 'object') {
        Object.keys(cf).forEach((k) => {
          const key = k.toLowerCase()
          if (key.includes('ema')) set.add('ema')
          else if (key.includes('rsi')) set.add('rsi')
          else if (key.includes('vol')) set.add('volume')
          else if (key.includes('fibo') || key.includes('fib')) set.add('fibo')
          else set.add(key.split('_')[0])
        })
      }
      return Math.max(4, set.size)
    } catch { return 4 }
  }

  return filteredOpps.map((r: any) => {
    const sym = String(r.symbol || '').toUpperCase()
    const meta = bySymbol.get(sym)
    const strength = Number(r.confluence_strength || 0)
    const pct = confluencePercent(strength, r?.confluence_factors)
    const nb = detectNearBreak(r?.confluence_factors, String(r?.target_trend || ''))
    const nearCritical = !!nb && ((nb.targetPct != null && nb.targetPct <= 0.05) || (nb.parentPct != null && nb.parentPct <= 0.05))
    const amberWarning = !!nb && nb.parentPct != null && nb.parentPct <= 1.0 && nb.parentPct >= 0.06
    const trend = deriveTrendSSOT(r)
    const item: OpportunityItem = {
      id: String(r.id || crypto.randomUUID()),
      symbol: sym,
      name: (meta as any)?.name || (meta as any)?.base_asset || null,
      logo_url: (meta as any)?.logo_url || null,
      recommendation: toRecommendation(r),
      objective: toObjective(trend ?? r?.target_trend),
      confluencePercent: pct,
      confluenceStrength: strength,
      status: (r as any)?.status ? String((r as any).status) : (r?.closed_at ? 'closed' : null),
      fractalLower: tfShort(minorForMajor(r?.target_timeframe) || r?.timeframe) || null,
      fractalUpper: tfShort(r?.target_timeframe) || null,
      nearBreak: nearCritical,
      confluences: listConfluences(r?.confluence_factors),
      uiWarning: amberWarning,
      publishedAt: (r as any)?.published_at ?? null,
      closedAt: (r as any)?.closed_at ?? null,
    }
    return item
  })
}

export async function getOpportunityById(id: string): Promise<any | null> {
  const { data, error } = await supabase
    .from('trading_opportunities')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return data as any
}

export async function listOportunidadesFechadas(limit = 120) {
  const { data, error } = await supabase
    .rpc('list_closed_opportunities_by_plan', { p_limit: limit })
  if (error) throw error
  const opps = Array.isArray(data) ? data : []

  const symbols = Array.from(new Set(opps.map((o: any) => String(o.symbol || '').trim()).filter(Boolean)))
  const assets = await fetchAssetsMeta(symbols)
  const bySymbol = new Map(assets.map((a) => [String(a.symbol).toUpperCase(), a]))

  return opps.map((r: any) => {
    const sym = String(r.symbol || '').toUpperCase()
    const meta = bySymbol.get(sym)
    const strength = Number(r.confluence_strength || 0)
    const pct = confluencePercent(strength, r?.confluence_factors)
    const nb = detectNearBreak(r?.confluence_factors, String(r?.target_trend || ''))
    const nearCritical = !!nb && ((nb.targetPct != null && nb.targetPct <= 0.05) || (nb.parentPct != null && nb.parentPct <= 0.05))
    const trend = deriveTrendSSOT(r)
    return {
      id: String(r.id || crypto.randomUUID()),
      symbol: sym,
      name: (meta as any)?.name || (meta as any)?.base_asset || null,
      logo_url: (meta as any)?.logo_url || null,
      recommendation: toRecommendation(r),
      objective: toObjective(trend),
      confluencePercent: pct,
      confluenceStrength: strength,
      status: 'closed',
      // Para encerradas, usamos o mesmo mapeamento de TF menor direto do fluxo ativo:
      // gatilho derivado do TF mandante (target_timeframe), caindo para o timeframe
      // original de detecção apenas se não houver mapping.
      fractalLower: tfShort(minorForMajor(r?.target_timeframe) || r?.timeframe) || null,
      fractalUpper: tfShort(String(r?.target_timeframe || '')),
      nearBreak: nearCritical,
      confluences: listConfluences(r?.confluence_factors),
      publishedAt: (r as any)?.published_at ?? null,
      closedAt: (r as any)?.closed_at ?? null,
    } as OpportunityItem
  })
}

export async function listOportunidadesByPublished(published: boolean): Promise<OpportunityItem[]> {
  const { data: oppsRaw, error } = await supabase
    .from('v_trading_opportunities_dedup')
    .select('id,symbol,timeframe,target_timeframe,opportunity_type,signal_type,target_trend,confluence_strength,confluence_factors,created_at,open_time,status,closed_at,closed_reason,is_published')
    .eq('is_published', published)
    .order('created_at', { ascending: false })
    .limit(100)
  if (error) throw error
  const opps = Array.isArray(oppsRaw) ? oppsRaw : []

  const symbols = Array.from(new Set(opps.map((o: any) => String(o.symbol || '').trim()).filter(Boolean)))
  const assets = await fetchAssetsMeta(symbols)
  const bySymbol = new Map(assets.map((a) => [String(a.symbol).toUpperCase(), a]))

  return opps.map((r: any) => {
    const sym = String(r.symbol || '').toUpperCase()
    const meta = bySymbol.get(sym)
    const strength = Number(r.confluence_strength || 0)
    const pct = confluencePercent(strength, r?.confluence_factors)
    const nb = detectNearBreak(r?.confluence_factors, String(r?.target_trend || ''))
    const nearCritical = !!nb && ((nb.targetPct != null && nb.targetPct <= 0.05) || (nb.parentPct != null && nb.parentPct <= 0.05))
    const amberWarning = !!nb && nb.parentPct != null && nb.parentPct <= 1.0 && nb.parentPct >= 0.06
    const trend = deriveTrendSSOT(r)
    const item: OpportunityItem = {
      id: String(r.id || crypto.randomUUID()),
      symbol: sym,
      name: (meta as any)?.name || (meta as any)?.base_asset || null,
      logo_url: (meta as any)?.logo_url || null,
      recommendation: toRecommendation(r),
      objective: toObjective(trend ?? r?.target_trend),
      confluencePercent: pct,
      confluenceStrength: strength,
      status: (r as any)?.status ? String((r as any).status) : (r?.closed_at ? 'closed' : null),
      fractalLower: tfShort(minorForMajor(r?.target_timeframe) || r?.timeframe) || null,
      fractalUpper: tfShort(r?.target_timeframe) || null,
      nearBreak: nearCritical,
      confluences: listConfluences(r?.confluence_factors),
      uiWarning: amberWarning,
    }
    return item
  })
}

export async function updateOpportunity(id: string, payload: {
  timeframe?: string | null
  target_timeframe?: string | null
  target_trend?: string | null
  opportunity_type?: string | null
  signal_type?: string | null
  confluence_factors?: any
}): Promise<void> {
  const update: any = {}
  if (typeof payload.timeframe !== 'undefined') update.timeframe = payload.timeframe
  if (typeof payload.target_timeframe !== 'undefined') update.target_timeframe = payload.target_timeframe
  if (typeof payload.target_trend !== 'undefined') update.target_trend = payload.target_trend
  if (typeof payload.opportunity_type !== 'undefined') update.opportunity_type = payload.opportunity_type
  if (typeof payload.signal_type !== 'undefined') update.signal_type = payload.signal_type
  if (typeof payload.confluence_factors !== 'undefined') update.confluence_factors = payload.confluence_factors
  const { error } = await supabase.from('trading_opportunities').update(update).eq('id', id)
  if (error) throw error
}

export async function publishOpportunity(id: string): Promise<void> {
  const oid = Number(id)
  if (!Number.isFinite(oid)) throw new Error('ID invalido')
  const { data, error } = await supabase.rpc('validate_and_publish_opportunity', { p_id: oid, p_allow_retarget: true })
  if (error) throw error
  const ok = (data as any)?.ok === true
  if (!ok) {
    const reasons = Array.isArray((data as any)?.reasons) ? (data as any).reasons.join(', ') : 'validation_failed'
    throw new Error(`Publicacao bloqueada pela validacao: ${reasons}`)
  }
}

export async function rejectOpportunity(id: string, reason?: string): Promise<void> {
  try {
    const { data: userData } = await supabase.auth.getUser()
    const reviewerId = userData?.user?.id || null
    const update: any = {
      status: 'closed',
      closed_at: new Date().toISOString(),
      closed_reason: 'rejected_by_master',
      is_published: false,
      reviewed_by: reviewerId,
    }
    if (reason && reason.trim()) {
      update.review_meta = { rejected_reason: String(reason).trim(), rejected_at: new Date().toISOString() }
    }
    const { error } = await supabase
      .from('trading_opportunities')
      .update(update)
      .eq('id', id)
    if (error) throw error
  } catch (e: any) {
    throw e
  }
}
